import { usePresence } from "@tamagui/use-presence";
import {
  isWeb,
  useEvent,
  useIsomorphicLayoutEffect,
  useSafeRef
} from "@tamagui/web";
import { useEffect, useMemo } from "react";
import { Animated } from "react-native";
const animatedStyleKey = {
  transform: true,
  opacity: true
};
const AnimatedView = Animated.View;
const AnimatedText = Animated.Text;
function useAnimatedNumber(initial) {
  const state = useSafeRef(
    null
  );
  if (!state.current) {
    state.current = {
      composite: null,
      val: new Animated.Value(initial),
      strategy: { type: "spring" }
    };
  }
  return {
    getInstance() {
      return state.current.val;
    },
    getValue() {
      return state.current.val["_value"];
    },
    stop() {
      var _a;
      (_a = state.current.composite) == null ? void 0 : _a.stop();
      state.current.composite = null;
    },
    setValue(next, { type, ...config } = { type: "spring" }) {
      var _a, _b;
      const val = state.current.val;
      if (type === "direct") {
        val.setValue(next);
      } else if (type === "spring") {
        (_a = state.current.composite) == null ? void 0 : _a.stop();
        const composite = Animated.spring(val, {
          ...config,
          toValue: next,
          useNativeDriver: !isWeb
        });
        composite.start();
        state.current.composite = composite;
      } else {
        (_b = state.current.composite) == null ? void 0 : _b.stop();
        const composite = Animated.timing(val, {
          ...config,
          toValue: next,
          useNativeDriver: !isWeb
        });
        composite.start();
        state.current.composite = composite;
      }
    }
  };
}
function useAnimatedNumberReaction(value, cb) {
  const onChange = useEvent((current) => {
    cb(current.value);
  });
  useEffect(() => {
    const id = value.getInstance().addListener(onChange);
    return () => {
      value.getInstance().removeListener(id);
    };
  }, [value, onChange]);
}
function useAnimatedNumberStyle(value, getStyle) {
  return getStyle(value.getInstance());
}
function createAnimations(animations) {
  AnimatedView["displayName"] = "AnimatedView";
  AnimatedText["displayName"] = "AnimatedText";
  return {
    isReactNative: true,
    animations,
    View: AnimatedView,
    Text: AnimatedText,
    useAnimatedNumber,
    useAnimatedNumberReaction,
    useAnimatedNumberStyle,
    usePresence,
    useAnimations: ({ props, onDidAnimate, style, state, presence }) => {
      const isExiting = (presence == null ? void 0 : presence[0]) === false;
      const sendExitComplete = presence == null ? void 0 : presence[1];
      const mergedStyles = style;
      const animateStyles = useSafeRef({});
      const animatedTranforms = useSafeRef([]);
      const animationsState = useSafeRef(null);
      if (!animationsState.current) {
        animationsState.current = /* @__PURE__ */ new WeakMap();
      }
      const args = [
        JSON.stringify(mergedStyles),
        JSON.stringify(state),
        isExiting,
        !!onDidAnimate
      ];
      const res = useMemo(() => {
        var _a;
        const runners = [];
        const completions = [];
        const nonAnimatedStyle = {};
        for (const key in mergedStyles) {
          const val = mergedStyles[key];
          if (!animatedStyleKey[key]) {
            nonAnimatedStyle[key] = val;
            continue;
          }
          if (key !== "transform") {
            animateStyles.current[key] = update(key, animateStyles.current[key], val);
            continue;
          }
          if (!val)
            continue;
          for (const [index, transform] of val.entries()) {
            if (!transform)
              continue;
            const tkey = Object.keys(transform)[0];
            const currentTransform = (_a = animatedTranforms.current[index]) == null ? void 0 : _a[tkey];
            animatedTranforms.current[index] = {
              [tkey]: update(tkey, currentTransform, transform[tkey])
            };
            animatedTranforms.current = [...animatedTranforms.current];
          }
        }
        const animatedStyle = {
          ...Object.fromEntries(
            Object.entries(animateStyles.current).map(([k, v]) => {
              var _a2;
              return [
                k,
                ((_a2 = animationsState.current.get(v)) == null ? void 0 : _a2.interopolation) || v
              ];
            })
          ),
          transform: animatedTranforms.current.map((r) => {
            var _a2;
            const key = Object.keys(r)[0];
            const val = ((_a2 = animationsState.current.get(r[key])) == null ? void 0 : _a2.interopolation) || r[key];
            return { [key]: val };
          })
        };
        return {
          runners,
          completions,
          style: [nonAnimatedStyle, animatedStyle]
        };
        function update(key, animated, valIn) {
          const [val, type] = getValue(valIn);
          const value = animated || new Animated.Value(val);
          if (isWeb) {
            if (animated && val === animated["_value"]) {
              return value;
            }
          }
          let interpolateArgs;
          if (type) {
            const curInterpolation = animationsState.current.get(value);
            interpolateArgs = getInterpolated(
              (curInterpolation == null ? void 0 : curInterpolation.current) ?? value["_value"],
              val,
              type
            );
            animationsState.current.set(value, {
              interopolation: value.interpolate(interpolateArgs),
              current: val
            });
          }
          if (value) {
            const animationConfig = getAnimationConfig(key, animations, props.animation);
            let resolve;
            const promise = new Promise((res2) => {
              resolve = res2;
            });
            completions.push(promise);
            runners.push(() => {
              value.stopAnimation();
              Animated[animationConfig.type || "spring"](value, {
                toValue: val,
                useNativeDriver: !isWeb,
                ...animationConfig
              }).start(({ finished }) => {
                if (finished) {
                  resolve();
                }
              });
            });
          }
          if (process.env.NODE_ENV === "development") {
            if (props["debug"]) {
              console.log(" \u{1F4A0} animate", key, `from ${value["_value"]} to`, valIn, `(${val})`, "type", type, "interpolate", interpolateArgs);
            }
          }
          return value;
        }
      }, args);
      useIsomorphicLayoutEffect(() => {
        res.runners.forEach((r) => r());
        let cancel = false;
        Promise.all(res.completions).then(() => {
          if (cancel)
            return;
          onDidAnimate == null ? void 0 : onDidAnimate();
          if (isExiting) {
            sendExitComplete == null ? void 0 : sendExitComplete();
          }
        });
        return () => {
          cancel = true;
        };
      }, args);
      if (process.env.NODE_ENV === "development") {
        if (props["debug"]) {
          console.log(`Returning animated`, res);
        }
      }
      return res;
    }
  };
}
function getInterpolated(current, next, postfix = "deg") {
  if (next === current) {
    current = next - 1e-9;
  }
  const inputRange = [current, next];
  const outputRange = [`${current}${postfix}`, `${next}${postfix}`];
  if (next < current) {
    inputRange.reverse();
    outputRange.reverse();
  }
  return {
    inputRange,
    outputRange
  };
}
function getAnimationConfig(key, animations, animation) {
  if (typeof animation === "string") {
    return animations[animation];
  }
  let type = "";
  let extraConf;
  if (Array.isArray(animation)) {
    type = animation[0];
    const conf = animation[1] && animation[1][key];
    if (conf) {
      if (typeof conf === "string") {
        type = conf;
      } else {
        type = conf.type || type;
        extraConf = conf;
      }
    }
  } else {
    const val = animation == null ? void 0 : animation[key];
    type = val == null ? void 0 : val.type;
    extraConf = val;
  }
  const found = animations[type];
  if (!found) {
    throw new Error(`No animation of type "${type}" for key "${key}"`);
  }
  return {
    ...found,
    ...extraConf
  };
}
function getValue(input) {
  if (typeof input !== "string") {
    return [input];
  }
  const [_, number, after] = input.match(/([-0-9]+)(deg|%|px)/) ?? [];
  return [+number, after];
}
export {
  AnimatedText,
  AnimatedView,
  createAnimations,
  useAnimatedNumber,
  useAnimatedNumberReaction,
  useAnimatedNumberStyle
};
//# sourceMappingURL=createAnimations.js.map
